'use client';

import { c as _c } from "react/compiler-runtime";
import type { FundingCheckout } from '@morfi/onramp-sdk/react';
import type { MoonPayWebProvider, WidgetEvent } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useEffectEvent, useRef } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { logger } from '@/lib/telemetry/logger';
import { FundingQuoteSummary } from './funding-quote-summary';
export function FundingHostedWidget(t0) {
  const $ = _c(46);
  const {
    provider,
    checkout
  } = t0;
  const t = useTranslations("onramp");
  const container = useRef(null);
  let t1;
  if ($[0] !== checkout) {
    t1 = event => {
      if (event.kind === "error") {
        logger.warn("onramp.hosted_widget_event_failed", {
          method: checkout.method,
          surface: "web",
          phase: "provider_failed",
          code: event.payload.code,
          attemptId: checkout.attemptId,
          hasProviderTransaction: Boolean(checkout.transactionId)
        });
      }
      checkout.hostedEvent(event);
    };
    $[0] = checkout;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onEvent = useEffectEvent(t1);
  let t2;
  if ($[2] !== checkout.method || $[3] !== onEvent) {
    t2 = () => {
      logger.warn("onramp.hosted_widget_setup_failed", {
        method: checkout.method,
        surface: "web",
        code: "widget_unavailable"
      });
      onEvent({
        kind: "error",
        payload: {
          code: "generic",
          message: "widget_unavailable"
        }
      });
    };
    $[2] = checkout.method;
    $[3] = onEvent;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const reportError = useEffectEvent(t2);
  const {
    paymentSignature,
    attemptId
  } = checkout;
  const quoteSignature = checkout.quote?.signature;
  let t3;
  if ($[5] !== attemptId || $[6] !== onEvent || $[7] !== paymentSignature || $[8] !== provider || $[9] !== quoteSignature || $[10] !== reportError) {
    t3 = () => {
      if (!container.current || !paymentSignature || !attemptId || quoteSignature !== paymentSignature) {
        return;
      }
      const quote = paymentSignature;
      const externalTransactionId = attemptId;
      const owned = document.createElement("div");
      owned.className = "h-full w-full";
      container.current.append(owned);
      let active = true;
      let frame;
      const mount = async function mount() {
        await Promise.resolve();
        if (!active) {
          return;
        }
        try {
          const result = await provider.setupWidget({
            container: owned,
            quote,
            externalTransactionId,
            onEvent(event_0) {
              if (active) {
                onEvent(event_0);
              }
            }
          });
          if (result.ok) {
            if (active) {
              frame = result.value;
            } else {
              result.value.dispose();
            }
          } else {
            if (active) {
              reportError();
            }
          }
        } catch {
          if (active) {
            reportError();
          }
        }
      };
      mount();
      return () => {
        active = false;
        owned.remove();
        frame?.dispose();
      };
    };
    $[5] = attemptId;
    $[6] = onEvent;
    $[7] = paymentSignature;
    $[8] = provider;
    $[9] = quoteSignature;
    $[10] = reportError;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== attemptId || $[13] !== paymentSignature || $[14] !== provider || $[15] !== quoteSignature) {
    t4 = [provider, paymentSignature, attemptId, quoteSignature];
    $[12] = attemptId;
    $[13] = paymentSignature;
    $[14] = provider;
    $[15] = quoteSignature;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  useEffect(t3, t4);
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" size="h-7.5 w-22.5" imageClassName="object-contain invert dark:invert-0" className="bg-transparent" src="/assets/onramp/moonpay.svg" alt="MoonPay" />;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t) {
    t6 = t("hosted_secure_checkout");
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = <div className="flex items-center justify-between text-caption">{t5}<span className="text-foreground-secondary">{t6}</span></div>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t) {
    t8 = t("hosted_title");
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t8) {
    t9 = <h3 className="text-heading-md font-medium">{t8}</h3>;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t) {
    t10 = t("hosted_description");
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <p className="text-caption text-foreground-secondary">{t10}</p>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== checkout) {
    t12 = <FundingQuoteSummary checkout={checkout} />;
    $[30] = checkout;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div ref={container} className="h-[28rem] w-full" />;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== checkout) {
    t14 = () => checkout.hostedEvent({
      kind: "close"
    });
    $[33] = checkout;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t) {
    t15 = t("hosted_return");
    $[35] = t;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15) {
    t16 = <AnimatedButton variant="tertiary" className="w-full" onClick={t14}>{t15}</AnimatedButton>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t16 || $[43] !== t7 || $[44] !== t9) {
    t17 = <div className="flex flex-col gap-4 rounded-control border border-border-primary bg-background-secondary p-5">{t7}{t9}{t11}{t12}{t13}{t16}</div>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t16;
    $[43] = t7;
    $[44] = t9;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  return t17;
}